import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import {
  Ruler, Square, ArrowUpDown, ArrowLeftRight, MousePointer2,
  Trash2, ChevronLeft, ChevronRight, Tag,
  ZoomIn, ZoomOut, Maximize2, X, Undo2, CornerDownRight, Minus, ChevronDown,
  FlipHorizontal, RotateCw, RotateCcw, ScanSearch, Focus, Hand,
} from "lucide-react";
import { drawAnnotations, regionCorners, computeFootPads, resolvePadSizeId, LINE_COLORS, FOOT_COLORS } from "../lib/annotationRenderer.js";
import { clampView, zoomViewAt, fitRegions } from "../lib/scanViewport.js";

const pxToMm = (px, dpi) => ((px / dpi) * 25.4).toFixed(1);
const dist = (x1, y1, x2, y2) => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
const mmToRuSize = (mm) => (!mm || isNaN(Number(mm)) ? null : Math.round(Number(mm) / 10 * 1.5 + 1));

const CORNER_HIT = 10;


// ── Custom tool dropdown ──────────────────────────────────────────────────────
function ToolDropdown({ tools, value, onChange, placeholder = "Инструмент" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = tools.find(t => t.id === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-2.5 h-7 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 transition-all"
      >
        {current ? (
          <>
            <span className="flex-shrink-0 text-slate-500">{current.icon}</span>
            <span>{current.label}</span>
          </>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <ChevronDown className="h-3 w-3 text-slate-400 ml-0.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 min-w-[130px]">
          {tools.map(t => (
            <button key={t.id} onClick={() => { onChange(t.id); setOpen(false); }}
              className={cn(
                "flex items-center gap-2.5 w-full px-3 py-1.5 text-xs text-left transition-colors",
                value === t.id ? "text-blue-600 bg-blue-50 font-semibold" : "text-slate-700 hover:bg-slate-50"
              )}>
              <span className="flex-shrink-0">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pad size dropdown ─────────────────────────────────────────────────────────
function PadSizeDropdown({ sizes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = sizes.find(s => s.id === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0 flex items-center gap-1">
      <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">Валик:</span>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2 h-7 rounded-lg text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 transition-all"
      >
        <span>{current?.name || "—"}</span>
        <ChevronDown className="h-3 w-3 text-purple-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 min-w-[130px]">
          {sizes.map(s => (
            <button key={s.id} onClick={() => { onChange(s.id); setOpen(false); }}
              className={cn(
                "flex items-center justify-between gap-3 w-full px-3 py-1.5 text-xs text-left transition-colors",
                value === s.id ? "text-purple-700 bg-purple-50 font-semibold" : "text-slate-700 hover:bg-slate-50"
              )}>
              <span>{s.name}</span>
              <span className="text-[10px] text-slate-400">{s.length_mm}×{s.width_mm}мм</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Foot size widget — flanks the Левая/Правая selector, replaces the old "Параметры стоп" tab ──
function FootSizeWidget({ side, align, scanParams, onScanParamsChange, allAnnotations }) {
  const data = scanParams[side] || {};
  const ruSize = mmToRuSize(data.footSizeMm);

  const measuredLength = allAnnotations
    .filter(a => a.foot === side || (side === "left" && !a.foot))
    .find(a => a.type === "size_line")?.distanceMm;

  useEffect(() => {
    if (measuredLength && !data.footSizeMm) {
      onScanParamsChange({ ...scanParams, [side]: { ...data, footSizeMm: String(Math.round(measuredLength)) } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredLength]);

  return (
    <div className={cn("flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-xl px-2.5 py-1.5", align === "right" && "flex-row-reverse")}>
      <input
        type="number"
        min="150"
        max="350"
        placeholder="—"
        value={data.footSizeMm || ""}
        onChange={e => onScanParamsChange({ ...scanParams, [side]: { ...data, footSizeMm: e.target.value } })}
        className="w-11 bg-transparent text-white text-xs text-center outline-none placeholder:text-white/40 [appearance:textfield]"
        title={side === "left" ? "Размер левой стопы, мм" : "Размер правой стопы, мм"}
      />
      <span className="text-white/50 text-[10px]">мм</span>
      {ruSize && <span className="viewer-size-ru text-white text-[10px] font-bold whitespace-nowrap">RU {ruSize}</span>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ImageViewer({
  pages, currentPage, onPageChange, dpi, annotations, onAnnotationsChange, imageKey,
  activeFoot = "left", onActiveFootChange,
  scanView = "original", onScanViewChange,
  onUndo, canUndo = false,
  onFlipPage, onRotatePage, onAutoDetectFeet,
  padSizes = [],
  onPadSizeChange,
  scanParams = {}, onScanParamsChange,
  allAnnotations = [],
}) {
  const [tool, setTool] = useState("pan");
  const [drawing, setDrawing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [localResizeAnns, setLocalResizeAnns] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [padSizesPerFoot, setPadSizesPerFoot] = useState({ left: null, right: null });

  const effectiveAnnotations = localResizeAnns ?? annotations;
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const zoom = view.zoom;
  const [spacePressed, setSpacePressed] = useState(false);
  const [panning, setPanning] = useState(false);
  const [natW, setNatW] = useState(0);
  const [natH, setNatH] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [imgMax, setImgMax] = useState({ w: 0, h: 0 });

  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const areaRef   = useRef(null);
  const geometryRef = useRef({ width: 0, height: 0, imageWidth: 0, imageHeight: 0, fitScale: 1 });
  const panRef = useRef(null);

  const page = pages[currentPage];
  const hasPage = Boolean(page);

  useEffect(() => {
    if (padSizes?.length) {
      const next = { left: padSizesPerFoot.left ?? padSizes[0].id, right: padSizesPerFoot.right ?? padSizes[0].id };
      if (next.left !== padSizesPerFoot.left || next.right !== padSizesPerFoot.right) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Catalog defaults arrive after mount.
        setPadSizesPerFoot(next);
        onPadSizeChange?.(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [padSizes]);

  const currentPadSizeId = padSizesPerFoot[activeFoot] ?? null;

  const handlePadSizeChange = (id) => {
    const next = { ...padSizesPerFoot, [activeFoot]: id };
    setPadSizesPerFoot(next);
    onPadSizeChange?.(next);
  };

  // ── Derived display size ──────────────────────────────────────────
  const fitScale = natW && natH && imgMax.w && imgMax.h ? Math.min(Math.max(1, imgMax.w - 32) / natW, Math.max(1, imgMax.h - 32) / natH) : 1;
  const displayW = natW ? natW * fitScale * zoom : undefined;
  const displayH = natH ? natH * fitScale * zoom : undefined;
  useLayoutEffect(() => {
    geometryRef.current = { width: imgMax.w, height: imgMax.h, imageWidth: natW, imageHeight: natH, fitScale };
    setView(previous => clampView(previous, geometryRef.current));
  }, [imgMax.w, imgMax.h, natW, natH, fitScale]);

  const getScale = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return 1;
    return img.getBoundingClientRect().width / img.naturalWidth;
  }, []);

  const evToImg = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scale = getScale();
    return {
      x: Math.max(0, Math.min(imgRef.current?.naturalWidth || 0, (e.clientX - rect.left) / scale)),
      y: Math.max(0, Math.min(imgRef.current?.naturalHeight || 0, (e.clientY - rect.top) / scale)),
    };
  }, [getScale]);

  const zoomIn = () => setView(previous => zoomViewAt(previous, previous.zoom * 1.25, { x: 0, y: 0 }, geometryRef.current));
  const zoomOut = () => setView(previous => zoomViewAt(previous, previous.zoom / 1.25, { x: 0, y: 0 }, geometryRef.current));
  const zoomReset = () => setView({ zoom: 1, x: 0, y: 0 });

  // Centre both foot regions in the same coordinate system used by zoom and drag.
  const zoomToFeet = () => {
    const regions = effectiveAnnotations.filter(a => a.type === "region");
    setView(fitRegions(regions, geometryRef.current));
  };

  // ── Redraw ────────────────────────────────────────────────────────
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.naturalWidth) return;

    const scale = getScale();
    canvas.width  = img.naturalWidth * scale;
    canvas.height = img.naturalHeight * scale;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const draggingPadFoot = resizing?.corner === "padOffset" ? effectiveAnnotations[resizing.annIdx]?.foot : null;
    drawAnnotations(ctx, {
      annotations: effectiveAnnotations,
      padSizes, padSizesPerFoot, dpi, scale, showLabels,
      selectedIndex: selected,
      activeFoot,
      draggingPadFoot,
    });

    if (drawing) {
      if (tool === "region") {
        const lx=Math.min(drawing.x1,drawing.x2), rx=Math.max(drawing.x1,drawing.x2);
        const ty=Math.min(drawing.y1,drawing.y2), by=Math.max(drawing.y1,drawing.y2);
        const color = FOOT_COLORS[activeFoot] || "#2563EB";
        ctx.save();
        ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.setLineDash([8,4]);
        ctx.strokeRect(lx*scale, ty*scale, (rx-lx)*scale, (by-ty)*scale);
        ctx.restore();
      } else {
        const { x1, y1, x2, y2 } = drawing;
        const color = LINE_COLORS[tool] || "#1976d2";
        ctx.save();
        ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.setLineDash([5,3]);
        ctx.beginPath(); ctx.moveTo(x1*scale, y1*scale); ctx.lineTo(x2*scale, y2*scale); ctx.stroke();
        if (showLabels) {
          const mm = pxToMm(dist(x1,y1,x2,y2), dpi);
          const mx=(x1+x2)/2*scale, my=(y1+y2)/2*scale;
          const label = `${mm} мм`;
          ctx.font = "bold 13px sans-serif";
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = `${color}cc`;
          ctx.beginPath(); ctx.roundRect(mx-tw/2-4, my-16, tw+8, 20, 4); ctx.fill();
          ctx.fillStyle="#fff"; ctx.fillText(label, mx-tw/2, my-1);
        }
        ctx.restore();
      }
    }
  }, [effectiveAnnotations, drawing, selected, dpi, tool, getScale, activeFoot, showLabels, padSizes, padSizesPerFoot, resizing]);

  useEffect(() => { redraw(); }, [redraw]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const ro = new ResizeObserver(() => redraw());
    ro.observe(img);
    return () => ro.disconnect();
  }, [redraw, page, fullscreen]);

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const update = () => setImgMax({ w: area.clientWidth, h: area.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(area);
    return () => ro.disconnect();
  }, [fullscreen, hasPage]);

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const onWheel = (e) => {
      e.preventDefault();
      const geometry = geometryRef.current;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? geometry.height : 1;
      if (e.ctrlKey || e.metaKey) {
        const rect = area.getBoundingClientRect();
        const point = { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 };
        const factor = Math.exp(-Math.max(-100, Math.min(100, e.deltaY * unit)) * 0.01);
        setView(previous => zoomViewAt(previous, previous.zoom * factor, point, geometry));
      } else {
        setView(previous => clampView({ ...previous, x: previous.x - (e.deltaX + (e.shiftKey ? e.deltaY : 0)) * unit, y: previous.y - (e.shiftKey ? 0 : e.deltaY) * unit }, geometry));
      }
    };
    area.addEventListener("wheel", onWheel, { passive: false });
    return () => area.removeEventListener("wheel", onWheel);
  }, [fullscreen, hasPage]);

  useEffect(() => {
    const typing = target => target instanceof Element && Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]'));
    const onDown = event => {
      if (event.code !== "Space" || !areaRef.current?.matches(":hover") || typing(event.target) || document.querySelector('[role="dialog"]')) return;
      event.preventDefault(); setSpacePressed(true);
    };
    const release = () => { setSpacePressed(false); panRef.current = null; setPanning(false); };
    const onUp = event => { if (event.code === "Space") release(); };
    document.addEventListener("keydown", onDown);
    document.addEventListener("keyup", onUp);
    window.addEventListener("blur", release);
    return () => { document.removeEventListener("keydown", onDown); document.removeEventListener("keyup", onUp); window.removeEventListener("blur", release); };
  }, []);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [fullscreen]);

  useLayoutEffect(() => {
    setView({ zoom: 1, x: 0, y: 0 });
    panRef.current = null; setPanning(false);
  }, [imageKey, currentPage, hasPage, fullscreen]);

  const startPan = event => {
    if (event.button !== 1 && !(event.button === 0 && (tool === "pan" || spacePressed || event.target === areaRef.current))) return;
    event.preventDefault(); event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    panRef.current = { x: event.clientX, y: event.clientY };
    setPanning(true);
  };
  const movePan = event => {
    if (!panRef.current) return;
    event.preventDefault(); event.stopPropagation();
    const dx = event.clientX - panRef.current.x, dy = event.clientY - panRef.current.y;
    panRef.current = { x: event.clientX, y: event.clientY };
    setView(previous => clampView({ ...previous, x: previous.x + dx, y: previous.y + dy }, geometryRef.current));
  };
  const endPan = event => {
    if (!panRef.current) return;
    event.preventDefault(); event.stopPropagation();
    panRef.current = null; setPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleMouseDown = useCallback((e) => {
    if (!page || tool === "pan") return;
    const { x, y } = evToImg(e);
    const scale = getScale();
    const hitR = CORNER_HIT / scale;

    if (tool === "select") {
      // Валик — проверяем первым: он сидит прямо на пересечении ширинной и метатарзальной
      // линий, где иначе конкурируют хит-зоны их концов/тела. Двигать можно только вдоль оси
      // метатарзальной линии (см. computeFootPads) — смещение хранится на самой линии
      // (padOffset), а не как отдельная аннотация.
      const pads = computeFootPads(effectiveAnnotations);
      for (const pad of pads) {
        const padSize = padSizes.find(s => s.id === resolvePadSizeId(padSizes, padSizesPerFoot, pad.foot));
        if (!padSize) continue;
        const padHitR = Math.max((padSize.length_mm / 25.4) * dpi, (padSize.width_mm / 25.4) * dpi) / 2;
        if (dist(x, y, pad.pt.x, pad.pt.y) < padHitR) {
          setSelected(pad.metIdx);
          setResizing({
            annIdx: pad.metIdx, corner: "padOffset", axis: pad.axis,
            startOffset: effectiveAnnotations[pad.metIdx]?.padOffset || 0, startX: x, startY: y,
          });
          return;
        }
      }
      // Check region corners
      for (let i = effectiveAnnotations.length - 1; i >= 0; i--) {
        const ann = effectiveAnnotations[i];
        if (ann.type !== "region") continue;
        const corners = regionCorners(ann);
        for (const [key, [cx, cy]] of Object.entries(corners)) {
          if (dist(x, y, cx, cy) < hitR) {
            setSelected(i); setResizing({ annIdx: i, corner: key }); return;
          }
        }
      }
      // Check line endpoints
      for (let i = effectiveAnnotations.length - 1; i >= 0; i--) {
        const ann = effectiveAnnotations[i];
        if (ann.type === "region") continue;
        if (dist(x, y, ann.x1, ann.y1) < hitR * 2) {
          setSelected(i); setResizing({ annIdx: i, corner: "p1" }); return;
        }
        if (dist(x, y, ann.x2, ann.y2) < hitR * 2) {
          setSelected(i); setResizing({ annIdx: i, corner: "p2" }); return;
        }
      }
      let found = -1;
      effectiveAnnotations.forEach((ann, i) => {
        if (ann.type === "region") {
          const lx=Math.min(ann.x1,ann.x2), rx=Math.max(ann.x1,ann.x2);
          const ty=Math.min(ann.y1,ann.y2), by=Math.max(ann.y1,ann.y2);
          if (x>=lx-hitR && x<=rx+hitR && y>=ty-hitR && y<=by+hitR) found = i;
        } else {
          const { x1, y1, x2, y2 } = ann;
          if (dist(x,y,x1,y1)<hitR*2 || dist(x,y,x2,y2)<hitR*2 || dist(x,y,(x1+x2)/2,(y1+y2)/2)<hitR*3) found = i;
        }
      });
      setSelected(found >= 0 ? found : null);
      return;
    }

    setDrawing({ x1: x, y1: y, x2: x, y2: y });
    setSelected(null);
  }, [tool, page, evToImg, annotations, effectiveAnnotations, getScale, padSizes, padSizesPerFoot, dpi]);

  const handleMouseMove = useCallback((e) => {
    if (!page) return;
    const { x, y } = evToImg(e);
    if (resizing) {
      const base = localResizeAnns ?? annotations;
      const ann = base[resizing.annIdx];
      const updated = { ...ann };
      if (resizing.corner === "tl") { updated.x1 = x; updated.y1 = y; }
      else if (resizing.corner === "tr") { updated.x2 = x; updated.y1 = y; }
      else if (resizing.corner === "bl") { updated.x1 = x; updated.y2 = y; }
      else if (resizing.corner === "br") { updated.x2 = x; updated.y2 = y; }
      // Edge handles move only one side, keeping the opposite edge (and so only width OR
      // only height). x1/x2 aren't guaranteed ordered (a prior corner drag may have flipped
      // the rect), so pick whichever field currently holds the min/max rather than assuming x1.
      else if (resizing.corner === "l") { if (ann.x1 <= ann.x2) updated.x1 = x; else updated.x2 = x; }
      else if (resizing.corner === "r") { if (ann.x1 <= ann.x2) updated.x2 = x; else updated.x1 = x; }
      else if (resizing.corner === "t") { if (ann.y1 <= ann.y2) updated.y1 = y; else updated.y2 = y; }
      else if (resizing.corner === "b") { if (ann.y1 <= ann.y2) updated.y2 = y; else updated.y1 = y; }
      else if (resizing.corner === "p1") { updated.x1 = x; updated.y1 = y; }
      else if (resizing.corner === "p2") { updated.x2 = x; updated.y2 = y; }
      else if (resizing.corner === "padOffset") {
        // Проецируем движение мыши на ось линии — перпендикулярная составляющая игнорируется,
        // так что валик физически не может сойти с метатарзальной линии.
        const { ux, uy } = resizing.axis;
        const proj = (x - resizing.startX) * ux + (y - resizing.startY) * uy;
        updated.padOffset = resizing.startOffset + proj;
      }
      setLocalResizeAnns(base.map((a, i) => i === resizing.annIdx ? updated : a));
      return;
    }
    if (drawing) setDrawing(prev => ({ ...prev, x2: x, y2: y }));
  }, [drawing, resizing, evToImg, annotations, localResizeAnns, page]);

  const handleMouseUp = useCallback(() => {
    if (resizing) {
      if (localResizeAnns) {
        let finalAnns = localResizeAnns;
        // Recalculate distanceMm when a line endpoint was moved
        if (resizing.corner === "p1" || resizing.corner === "p2") {
          const ann = localResizeAnns[resizing.annIdx];
          if (ann && ann.type !== "region") {
            const d = dist(ann.x1, ann.y1, ann.x2, ann.y2);
            const updated = { ...ann, distanceMm: pxToMm(d, dpi) };
            finalAnns = localResizeAnns.map((a, i) => i === resizing.annIdx ? updated : a);
          }
        }
        onAnnotationsChange(finalAnns);
        setLocalResizeAnns(null);
      }
      setResizing(null);
      return;
    }
    if (!drawing) return;
    const { x1, y1, x2, y2 } = drawing;
    if (tool === "region") {
      const w = Math.abs(x2-x1), h = Math.abs(y2-y1);
      if (w > 10 && h > 10) {
        // Only one region per foot — drawing a new one replaces the old instead of stacking.
        const base = annotations.filter(a => !(a.type === "region" && a.foot === activeFoot));
        onAnnotationsChange([...base, { type: "region", x1, y1, x2, y2, foot: activeFoot }]);
      }
    } else {
      const d = dist(x1, y1, x2, y2);
      if (d > 3) {
        // Same one-per-foot rule for the structural measurements (Разм./Шир./Пят.) — the free
        // "Линейка" (measure) tool is exempt and can be added any number of times.
        const base = tool === "measure"
          ? annotations
          : annotations.filter(a => !(a.type === tool && a.foot === activeFoot));
        onAnnotationsChange([...base, { type: tool, x1, y1, x2, y2, distanceMm: pxToMm(d, dpi), foot: activeFoot }]);
      }
    }
    setDrawing(null);
  }, [drawing, resizing, localResizeAnns, dpi, annotations, onAnnotationsChange, tool, activeFoot]);

  const deleteSelected = () => {
    if (selected === null) return;
    onAnnotationsChange(effectiveAnnotations.filter((_, i) => i !== selected));
    setLocalResizeAnns(null);
    setSelected(null);
  };

  // ── Empty state ───────────────────────────────────────────────────
  if (!page) {
    return (
      <div className="image-viewer">
        <div className="viewer-empty">
          <span className="viewer-empty-icon">🖨</span>
          <span className="viewer-empty-text">Подключите сканер и нажмите «Сканировать»</span>
        </div>
      </div>
    );
  }

  // ── Config ────────────────────────────────────────────────────────
  const GENERAL_TOOLS = [
    { id: "pan", label: "Перемещение", icon: <Hand className="h-3.5 w-3.5" />, title: "Перемещение снимка (пробел или средняя кнопка мыши)" },
    { id: "select",  label: "Выбор",   icon: <MousePointer2 className="h-3.5 w-3.5" />, title: "Выбрать/изменить" },
    { id: "measure", label: "Линейка", icon: <Ruler className="h-3.5 w-3.5" />,         title: "Произвольное расстояние" },
    { id: "region",  label: "Область", icon: <Square className="h-3.5 w-3.5" />,        title: "Выделить область" },
  ];
  const MEASURE_TOOLS = [
    { id: "size_line",       label: "Длина", icon: <ArrowUpDown className="h-3.5 w-3.5" />,    title: "Размерная (большой палец → пятка)" },
    { id: "width_line",      label: "Ширина", icon: <ArrowLeftRight className="h-3.5 w-3.5" />, title: "Ширина стопы (поперёк)" },
    { id: "metatarsal_line", label: "Метатарзальная", icon: <CornerDownRight className="h-3.5 w-3.5" />,title: "Метатарзальная (центр ширины → пятка)" },
    { id: "heel_line",       label: "Пятка", icon: <Minus className="h-3.5 w-3.5" />, title: "Пяточная (ширина пятки)" },
  ];
  const VIEWS = [
    { id: "wb",       label: "Белый фон" },
    { id: "heat",     label: "Тепловая" },
  ];

  const hasMetPad = effectiveAnnotations.some(a => a.type === "width_line")
    && effectiveAnnotations.some(a => a.type === "metatarsal_line")
    && padSizes?.length > 0;

  const generalToolValue = GENERAL_TOOLS.some(t => t.id === tool) ? tool : "";

  // ── Toolbar ───────────────────────────────────────────────────────
  const toolbar = (
    <div className="flex items-center flex-wrap gap-1.5 px-2 py-1.5 bg-white border-b border-slate-200 min-h-10 flex-shrink-0">

      {/* General tool dropdown */}
      <ToolDropdown tools={GENERAL_TOOLS} value={generalToolValue} onChange={setTool} />

      <div className="w-px h-5 bg-slate-200 flex-shrink-0" />

      {/* Medical measurement tools */}
      <div className="flex items-center gap-0.5 bg-blue-50 rounded-lg p-0.5 flex-shrink-0">
        {MEASURE_TOOLS.map(t => (
          <button key={t.id} title={t.title} onClick={() => setTool(t.id)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all",
              tool === t.id ? "bg-white shadow-sm" : "text-slate-400 hover:text-slate-700"
            )}
            style={tool === t.id ? { color: LINE_COLORS[t.id] } : {}}>
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Pad size dropdown (conditional) */}
      {hasMetPad && (
        <>
          <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
          <PadSizeDropdown sizes={padSizes} value={currentPadSizeId} onChange={handlePadSizeChange} />
        </>
      )}

      {/* Labels toggle */}
      <button title={showLabels ? "Скрыть все подписи" : "Показать подписи"}
        onClick={() => setShowLabels(v => !v)}
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all flex-shrink-0",
          showLabels ? "bg-blue-50 text-blue-600" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
        )}>
        <Tag className="h-3.5 w-3.5" />
        <span>Подписи</span>
      </button>

      {/* Delete selected */}
      {selected !== null && (
        <button onClick={deleteSelected}
          className="flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex-shrink-0">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Right: clear + pagination */}
      <div className="ml-auto flex items-center gap-1 flex-shrink-0">
        {annotations.length > 0 && (
          <button onClick={() => { onAnnotationsChange([]); setSelected(null); }}
            className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors">
            Очистить
          </button>
        )}
        {pages.length > 1 && (
          <div className="flex items-center gap-1">
            <button className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors"
              disabled={currentPage === 0} onClick={() => onPageChange(currentPage - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-slate-500 font-medium">{currentPage + 1}/{pages.length}</span>
            <button className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors"
              disabled={currentPage >= pages.length - 1} onClick={() => onPageChange(currentPage + 1)}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // The image and annotations share one transform. Viewport controls stay outside that transform.
  const canvasArea = (
    <div className="viewer-canvas-outer">
      <div className="viewer-controls absolute top-3 left-3 right-3 z-10 flex flex-wrap items-start gap-2 pointer-events-none">

      {/* TOP-LEFT: View mode switcher */}
      {onScanViewChange && (
        <div className="flex items-center gap-0.5 bg-black/30 backdrop-blur-sm rounded-xl p-1 pointer-events-auto">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => onScanViewChange(v.id)}
              className={cn(
                "px-2.5 py-1 rounded-lg text-xs font-semibold transition-all",
                scanView === v.id ? "bg-white/90 text-slate-900" : "text-white/65 hover:text-white/90"
              )}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* TOP-CENTER: foot size (left) + foot selector + foot size (right) */}
      {onActiveFootChange && (
        <div className="order-3 basis-full flex items-center justify-center flex-wrap gap-2 pointer-events-auto">
          {onScanParamsChange && (
            <FootSizeWidget side="left" align="left" scanParams={scanParams} onScanParamsChange={onScanParamsChange} allAnnotations={allAnnotations} />
          )}
          <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-xl p-1">
            {[
              { side: "left",  label: "Левая",  color: "bg-blue-500" },
              { side: "right", label: "Правая", color: "bg-green-500" },
            ].map(f => (
              <button key={f.side} onClick={() => onActiveFootChange(f.side)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all",
                  activeFoot === f.side ? `${f.color} text-white shadow-sm` : "text-white/60 hover:text-white/90"
                )}>
                {f.label}
              </button>
            ))}
          </div>
          {onScanParamsChange && (
            <FootSizeWidget side="right" align="right" scanParams={scanParams} onScanParamsChange={onScanParamsChange} allAnnotations={allAnnotations} />
          )}
        </div>
      )}

      {/* TOP-RIGHT: Auto-detect + Rotate + Flip + Undo */}
      <div className="ml-auto flex items-center gap-1.5 pointer-events-auto">
        {onAutoDetectFeet && (
          <button
            title="Определить стопы заново"
            onClick={onAutoDetectFeet}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <ScanSearch className="h-4 w-4" />
          </button>
        )}
        {onRotatePage && (
          <button
            title="Повернуть против часовой"
            onClick={() => onRotatePage(-1)}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
        {onRotatePage && (
          <button
            title="Повернуть по часовой"
            onClick={() => onRotatePage(1)}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <RotateCw className="h-4 w-4" />
          </button>
        )}
        {onFlipPage && (
          <button
            title="Отразить по горизонтали"
            onClick={onFlipPage}
            className="p-2 rounded-xl backdrop-blur-sm bg-black/30 text-white hover:bg-black/50 transition-all"
          >
            <FlipHorizontal className="h-4 w-4" />
          </button>
        )}
        <button
          title="Отменить (Ctrl+Z)"
          onClick={canUndo ? onUndo : undefined}
          className={cn(
            "p-2 rounded-xl backdrop-blur-sm transition-all",
            canUndo
              ? "bg-black/30 text-white hover:bg-black/50"
              : "bg-black/15 text-white/25 cursor-default"
          )}
        >
          <Undo2 className="h-4 w-4" />
        </button>
      </div>
      </div>

      {/* BOTTOM-LEFT: Zoom + Fullscreen */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-0.5 bg-black/30 backdrop-blur-sm rounded-xl p-1">
        <button title="Уменьшить" aria-label="Уменьшить" onClick={zoomOut}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <button title="Уместить весь снимок" aria-label="Уместить весь снимок" onClick={zoomReset}
          className="w-11 text-center text-[11px] font-bold text-white hover:bg-white/10 rounded-lg py-1 transition-all">
          {Math.round(fitScale * zoom * 100)}%
        </button>
        <button title="Увеличить" aria-label="Увеличить" onClick={zoomIn}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <button title="Уместить стопы" onClick={zoomToFeet}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          <Focus className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-white/25 mx-0.5" />
        <button
          title={fullscreen ? "Выйти (Esc)" : "На весь экран"}
          onClick={() => setFullscreen(f => !f)}
          className="p-1.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-all">
          {fullscreen ? <X className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className={`viewer-canvas-area ${panning ? "is-panning" : tool === "pan" || spacePressed ? "can-pan" : ""}`} ref={areaRef}
        title="Перемещение: рука, пробел + мышь или прокрутка. Масштаб: Ctrl/⌘ + прокрутка или жест сведения пальцев."
        onPointerDownCapture={startPan} onPointerMoveCapture={movePan} onPointerUpCapture={endPan} onPointerCancelCapture={endPan}>
        <div className="viewer-canvas-wrap" style={{ width: displayW, height: displayH, transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px)` }}>
          <img
            ref={imgRef}
            className="viewer-img"
            src={page.base64}
            alt={`Страница ${currentPage + 1}`}
            style={displayW ? { width: displayW, height: displayH } : { maxWidth: imgMax.w || "100%", maxHeight: imgMax.h || "100%" }}
            onLoad={() => {
              const img = imgRef.current;
              if (img) { setNatW(img.naturalWidth); setNatH(img.naturalHeight); }
              redraw();
            }}
            draggable={false}
          />
          <canvas
            ref={canvasRef}
            className={`viewer-canvas ${tool === "select" ? "select" : tool === "region" ? "crosshair" : "measuring"}`}
            onPointerDown={event => { if (event.button !== 0) return; event.currentTarget.setPointerCapture(event.pointerId); handleMouseDown(event); }}
            onPointerMove={handleMouseMove}
            onPointerUp={event => { handleMouseUp(); if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }}
            onPointerCancel={() => { setDrawing(null); setResizing(null); setLocalResizeAnns(null); }}
          />
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col bg-white">
        {toolbar}
        {canvasArea}
      </div>
    );
  }

  return (
    <div className="image-viewer">
      {toolbar}
      {canvasArea}
    </div>
  );
}
